import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import { ChevronDown, ChevronRight, AlertTriangle, MoreHorizontal, Plus } from 'lucide-react';
import {
  getDataset, listDatasetVersions, renameDataset, createDataset, replaceRows, deleteDataset,
  refreshEvaluations, getItemSources, listSources, runSource, listSourceRuns, removeSource,
} from 'features/datasets/api/datasetsApi';
import SourceDialog from 'features/datasets/components/SourceDialog';
import SourcesDialog from 'features/datasets/components/SourcesDialog';
import ExportDialog from 'features/datasets/components/ExportDialog';
import VersionsDialog from 'features/datasets/components/VersionsDialog';
import DatasetGrid from 'features/datasets/components/DatasetGrid';
import DatasetRowDrawer from 'features/datasets/components/DatasetRowDrawer';
import SaveDatasetDialog, { isPlaceholderName } from 'features/datasets/components/SaveDatasetDialog';
import { runOutcome } from 'features/datasets/utils/sources';
import LabelMenu from 'features/datasets/components/LabelMenu';
import { createPlayground } from 'features/playground/api/playgroundApi';
import TraceDetailPage from 'features/agent-analytics/pages/TraceDetailPage';
import AgentRunDetailPage from 'features/agent-analytics/pages/AgentRunDetailPage';
import { useHideGlobalTopBar } from 'shared/context/BreadcrumbContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import { usePermissions, PERMISSIONS } from 'shared/hooks/usePermissions';
import { activityToolbarButtonClass } from 'features/activity/styles/dataTableStyles';
import 'features/datasets/components/DatasetWorkspace.css';

// Loaded when a conversation link is opened (it brings the markdown renderer with it).
const ConversationDetailDrawer = React.lazy(() => import('features/agent-analytics/components/ConversationDetailDrawer'));

/** What a new dataset is before it exists: nothing on the server yet. */
const DRAFT = { id: null, name: '', columns: ['input'], derived_columns: [], items: [] };

const primaryBtn = 'inline-flex h-8 shrink-0 items-center gap-1 rounded-md bg-brand px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover disabled:opacity-50';
const addBtn = 'inline-flex h-8 shrink-0 items-center gap-1 rounded-md bg-[color:var(--trodo-primary)] px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:opacity-90 disabled:opacity-50';

/** A small menu that closes on outside click or Escape. */
function useMenu() {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return { open, setOpen, box };
}

const menuClass = 'absolute right-0 top-[calc(100%+4px)] z-30 w-56 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] p-1 shadow-token-lg';
const addMenuClass = 'absolute left-0 top-[calc(100%+4px)] z-30 w-56 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] p-1 shadow-token-lg';
function MenuItem({ children, onClick, disabled = false, danger = false, title }) {
  return (
    <button type="button" role="menuitem" onClick={onClick} disabled={disabled} title={title}
      className={`flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-[12px] hover:bg-surface-hover disabled:opacity-50 ${danger ? 'text-danger' : 'text-[color:var(--text-primary)]'}`}>
      {children}
    </button>
  );
}

export default function DatasetDetailPage({ datasetId, promptId = null }) {
  // /datasets/new is a draft: created on the server only when named and saved.
  const isDraft = datasetId === 'new';
  const { confirm: appConfirm } = useAppDialog();
  const { team } = useParams();
  const navigate = useNavigate();
  const canWrite = usePermissions().can(PERMISSIONS.APP_USE);
  useHideGlobalTopBar(true);

  const [dataset, setDataset] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const gridRef = useRef(null);
  const [gridDirty, setGridDirty] = useState(false);
  const [savingGrid, setSavingGrid] = useState(false);
  const [gridKey, setGridKey] = useState(0);
  const [saveDialog, setSaveDialog] = useState(false);

  const [versions, setVersions] = useState([]);
  const [originOverlay, setOriginOverlay] = useState(null);
  const [runOverlay, setRunOverlay] = useState(null);
  const [conversationOverlay, setConversationOverlay] = useState(null);
  const [drawerRowId, setDrawerRowId] = useState(null);
  const [drawerTab, setDrawerTab] = useState('fields');
  const [drawerRevision, setDrawerRevision] = useState(0);
  const createdRef = useRef(null);
  const reopenIndex = useRef(null);
  const [expiredItemIds, setExpiredItemIds] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const more = useMenu();
  const addMenu = useMenu();

  const load = useCallback(async () => {
    setError(null);
    if (isDraft) { setDataset(DRAFT); setName(''); setVersions([]); return; }
    try {
      const [d, vers] = await Promise.all([getDataset(datasetId, { summary: true }), listDatasetVersions(datasetId)]);
      setDataset(d);
      setName(d.name || '');
      setVersions(vers);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
  }, [datasetId, isDraft]);

  // Sources: loaded beside the dataset. While one is running the page polls,
  // and when it settles the rows are reloaded (only when the grid is clean,
  // so nothing unsaved is lost).
  const [sources, setSources] = useState([]);
  const [sourceDialog, setSourceDialog] = useState(null);   // null | { source? }
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [exportDialog, setExportDialog] = useState(null);   // null | { itemIds: null | [...] }
  const [versionsDialog, setVersionsDialog] = useState(false);
  const [notice, setNotice] = useState(null);
  const loadSources = useCallback(async () => {
    if (isDraft) return [];
    try {
      const s = await listSources(datasetId);
      const list = Array.isArray(s) ? s : [];
      setSources(list);
      return list;
    } catch { return []; }
  }, [datasetId, isDraft]);
  useEffect(() => { loadSources(); }, [loadSources]);
  const running = sources.some((s) => s.last_status === 'queued' || s.last_status === 'running');
  // Sync reruns dataset_sources only. An agent row (id agent:<workflow>) is a
  // published dataset_append step, not a source run. Rerunning it would start
  // the whole agent. Sync leaves those rows alone and says so.
  const syncableSources = useMemo(() => sources.filter((s) => !s.agent && s.id), [sources]);
  const agentSources = useMemo(() => sources.filter((s) => s.agent), [sources]);
  const syncBusy = syncing || running;
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(async () => {
      const next = await loadSources();
      if (!next.some((s) => s.last_status === 'queued' || s.last_status === 'running') && !gridDirty) {
        await load();
        setGridKey((k) => k + 1);
      }
    }, 2500);
    return () => clearInterval(t);
  }, [running, loadSources, load, gridDirty]);
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  const failWith = (e) => setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
  const sourceSaved = async () => { await loadSources(); };
  // The run is queued and finishes on the worker: wait for THIS run to settle, then say what it did
  // (rows added, nothing matched the period, or everything was already in the dataset).
  const awaitRun = async (src, feed) => {
    if (!feed?.id) return null;
    for (let i = 0; i < 60; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      let runs = [];
      try { runs = await listSourceRuns(datasetId, src.id); } catch { runs = []; }
      const mine = runs.find((r) => r.id === feed.id);
      if (mine && !['queued', 'running'].includes(mine.status)) return mine;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => { setTimeout(r, 2000); });
    }
    return null;
  };
  const reportRun = async (src, feed) => {
    const done = await awaitRun(src, feed);
    if (!done) return;
    const out = runOutcome(src, done);
    if (out.tone === 'failed') setError(out.text); else setNotice(out.text);
  };
  const sourceRun = async (src) => {
    try {
      const out = await runSource(datasetId, src.id);
      await loadSources();
      reportRun(src, out?.feed).catch(() => {});
    } catch (e) { failWith(e); }
  };
  const sourceRemove = async (src) => {
    const ok = await appConfirm('The rows it added stay in the dataset.', { title: 'Remove this source', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try { await removeSource(datasetId, src.id); await loadSources(); } catch (e) { failWith(e); }
  };

  // Sync reruns every dataset source. Each run only inserts rows the dataset
  // does not already hold, and credits are charged for rows written, not
  // skips or failures. Agent sources are not started.
  const syncSources = async () => {
    if (!syncableSources.length || syncBusy || gridDirty) return;
    setSyncing(true);
    setError(null);
    const agentNote = agentSources.length
      ? ` ${agentSources.length} agent source${agentSources.length === 1 ? '' : 's'} ${agentSources.length === 1 ? 'is' : 'are'} not rerun — ${agentSources.length === 1 ? 'it writes' : 'they write'} only when the agent runs.`
      : '';
    try {
      const results = await Promise.allSettled(syncableSources.map((src) => runSource(datasetId, src.id)));
      await loadSources();
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length) failWith(failed[0].reason);
      else {
        setNotice(`Syncing ${syncableSources.length} source${syncableSources.length === 1 ? '' : 's'}.${agentNote}`);
        // One source: say what its run did. Several: add the outcomes up.
        if (syncableSources.length === 1) reportRun(syncableSources[0], results[0].value?.feed);
        else {
          Promise.all(syncableSources.map((src, i) => awaitRun(src, results[i].value?.feed).then((d) => ({ src, d })))).then((all) => {
            const fails = all.filter((x) => x.d && x.d.status !== 'done');
            const added = all.reduce((n, x) => n + (Number(x.d?.rows_written) || 0), 0);
            if (fails.length) setError(runOutcome(fails[0].src, fails[0].d).text);
            else if (added) setNotice(`Synced: ${added.toLocaleString()} new row${added === 1 ? '' : 's'} added.`);
            else if (all.every((x) => x.d)) setNotice(`No new data was added by any of the ${all.length} sources.`);
          });
        }
      }
    } finally { setSyncing(false); }
  };

  // Re-snapshot the verdicts: one row from the drawer, or every unit row from the menu.
  const refreshEvals = async (itemId = null) => {
    if (isDraft) return;
    setError(null);
    try {
      const result = await refreshEvaluations(datasetId, itemId ? { itemId } : {});
      const freshItems = result?.dataset?.items;
      if (Array.isArray(freshItems) && freshItems.length && freshItems.length <= 50) {
        gridRef.current?.setEvaluations?.(Object.fromEntries(freshItems.map((it) => [it.id, it.evaluations || []])));
      } else if (Array.isArray(result?.updates) && result.updates.length) {
        gridRef.current?.setEvaluations?.(Object.fromEntries(result.updates.map((u) => [u.id, u.evaluations || []])));
        await gridRef.current?.reload?.();
      } else {
        await gridRef.current?.reload?.();
      }
      setDrawerRevision((v) => v + 1);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  };

  // A thumbs given in the drawer updates the row's snapshot locally.
  const onEvaluationFeedback = (itemId, ev, saved) => {
    if (!itemId || !ev?.eval_run_id) return;
    const feedback = saved ? {
      thumbs: saved.thumbs, corrected_passed: saved.corrected_passed ?? null, reason: saved.reason_text || null, user_id: saved.user_id ?? null,
    } : null;
    gridRef.current?.patchEvaluation?.(itemId, ev.eval_run_id, feedback);
    setDrawerRevision((v) => v + 1);
  };

  useEffect(() => { load(); }, [load]);

  // ?import=1 on the draft opens the file picker at once.
  const [searchParams] = useSearchParams();
  const wantsImport = isDraft && searchParams.get('import') === '1';
  useEffect(() => {
    if (!wantsImport || !dataset) return undefined;
    const t = setTimeout(() => gridRef.current?.openImport?.(), 50);
    return () => clearTimeout(t);
  }, [wantsImport, dataset]);

  // Leaving with unsaved rows loses them: the browser asks first.
  useEffect(() => {
    if (!gridDirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [gridDirty]);

  // The draft's own save: create it, named, then write its rows, then move to its real address.
  const draftDetailsRef = useRef({ name: '', description: '' });
  const persistDraft = async (columns, rows, derived) => {
    const { name: trimmed, description } = draftDetailsRef.current;
    if (!createdRef.current) {
      const created = await createDataset(trimmed, { columns, description: description || undefined });
      createdRef.current = created.id;
    }
    if (rows.length || (derived && derived.length)) await replaceRows(createdRef.current, columns, rows, derived);
    navigate(`/${encodeURIComponent(team)}/datasets/${createdRef.current}`, { replace: true });
  };

  const runSave = async () => {
    setSavingGrid(true);
    try { await gridRef.current?.save(); } catch (e) { failWith(e); } finally { setSavingGrid(false); }
  };

  const saveGrid = async () => {
    setError(null);
    if (isDraft) {
      const typed = (name || '').trim();
      if (isPlaceholderName(typed)) { setSaveDialog(true); return; }
      draftDetailsRef.current = { name: typed, description: draftDetailsRef.current.description };
    }
    await runSave();
  };

  const saveDialogDone = async ({ name: n, description }) => {
    setSaveDialog(false);
    setName(n);
    draftDetailsRef.current = { name: n, description };
    await runSave();
  };

  const commitName = async () => {
    if (isDraft) return;
    const trimmed = (name || '').trim();
    if (!dataset || !trimmed || trimmed === dataset.name) { setName(dataset?.name || ''); return; }
    try {
      const updated = await renameDataset(datasetId, trimmed);
      setDataset((d) => ({ ...d, name: updated.name }));
      setName(updated.name);
    } catch (e) {
      failWith(e);
      setName(dataset.name || '');
    }
  };

  const handleDelete = async () => {
    const ok = await appConfirm(`"${dataset.name}" and its rows will be deleted.`, { title: 'Delete dataset', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try {
      await deleteDataset(datasetId);
      navigate(`/${encodeURIComponent(team)}/datasets`);
    } catch (e) { failWith(e); }
  };

  const onSaved = (saved) => {
    if (saved) {
      const { items, ...rest } = saved;
      setDataset((d) => ({
        ...d,
        ...rest,
        item_count: rest.item_count != null ? rest.item_count : (Array.isArray(items) ? items.length : d.item_count),
      }));
    } else getDataset(datasetId, { summary: true }).then((d) => setDataset((cur) => ({ ...cur, ...d }))).catch(() => {});
    // Every save is a version.
    listDatasetVersions(datasetId).then(setVersions).catch(() => {});
    reopenIndex.current = drawerRowId ? gridRef.current?.getRowDrawerContext?.(drawerRowId)?.rowIndex ?? null : null;
    setDrawerRowId(null);
    setGridKey((k) => k + 1);
  };
  useEffect(() => {
    if (reopenIndex.current == null) return;
    const ctx = gridRef.current?.getRowDrawerContextByIndex?.(reopenIndex.current);
    reopenIndex.current = null;
    if (ctx?.rowId) setDrawerRowId(ctx.rowId);
  }, [gridKey]);

  const drawerContext = useMemo(
    () => (drawerRowId ? gridRef.current?.getRowDrawerContext?.(drawerRowId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [drawerRowId, drawerRevision, gridKey],
  );
  const retried = useRef(null);
  useEffect(() => {
    if (!drawerRowId) { retried.current = null; return; }
    if (!drawerContext && retried.current !== drawerRowId) {
      retried.current = drawerRowId;
      setDrawerRevision((v) => v + 1);
    }
  }, [drawerRowId, drawerContext]);

  const openRowDrawer = (rowId, tab = 'fields') => {
    if (!rowId) { setDrawerRowId(null); return; }
    setDrawerRowId(rowId);
    setDrawerTab(tab);
    setDrawerRevision((v) => v + 1);
  };
  const refreshDrawerSnapshot = () => setDrawerRevision((v) => v + 1);
  const navigateDrawerRow = async (delta) => {
    if (!drawerContext) return;
    const target = drawerContext.rowIndex + delta;
    if (target < 0) return;
    const next = gridRef.current?.getRowDrawerContextByIndex?.(target);
    if (next?.rowId) { openRowDrawer(next.rowId, drawerTab); return; }
    const id = await gridRef.current?.openGlobalIndex?.(target);
    if (id) openRowDrawer(id, drawerTab);
  };

  const onPageItems = useCallback((items) => {
    const ids = (items || []).filter((it) => it.unit_kind && it.unit_kind !== 'manual').map((it) => it.id);
    if (!ids.length || isDraft) { setExpiredItemIds([]); return; }
    getItemSources(datasetId, { itemIds: ids }).then((r) => setExpiredItemIds(r?.expired || [])).catch(() => {});
  }, [datasetId, isDraft]);

  const openOrigin = (ref) => {
    if (!ref) return;
    if (ref.kind === 'verdict' && ref.lineage_id) { navigate(`/${encodeURIComponent(team)}/agent-analytics/evals?eval=${ref.lineage_id}${ref.eval_run_id ? `&run=${ref.eval_run_id}` : ''}`); return; }
    if (ref.kind === 'span' && ref.span_id) { setOriginOverlay({ spanId: ref.span_id }); return; }
    if (ref.kind === 'conversation' && ref.conversation_id) { setConversationOverlay({ conversationId: ref.conversation_id }); return; }
    if (ref.run_id) setRunOverlay({ runId: ref.run_id, initialSpanId: ref.span_id || undefined });
    else if (ref.span_id) setOriginOverlay({ spanId: ref.span_id });
  };

  if (!dataset) {
    return error
      ? <div className="p-6 text-[12px] text-danger">{error}</div>
      : <PageLoader label="Loading dataset" />;
  }

  const showSave = canWrite && (gridDirty || isDraft);
  // A draft with no rows yet can start from a source: the dialog creates it, named, then fills it.
  const draftHasRows = isDraft && gridDirty;
  const rowCount = dataset.item_count != null ? Number(dataset.item_count) : (dataset.items || []).length;

  return (
    <div className="ds-workspace relative flex h-full min-h-0 flex-col bg-[color:var(--surface-base)]">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-[color:var(--border-default)] px-4">
        {promptId ? (
          <>
            <Link to={`/${encodeURIComponent(team)}/prompts`} className="text-[13px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">Prompts</Link>
            <ChevronRight className="h-3 w-3 text-[color:var(--text-muted)]" />
            <Link to={`/${encodeURIComponent(team)}/prompts/${promptId}/datasets`} className="text-[13px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">Datasets</Link>
          </>
        ) : (
          <Link to={`/${encodeURIComponent(team)}/datasets`} className="text-[13px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">Datasets</Link>
        )}
        <ChevronRight className="h-3 w-3 text-[color:var(--text-muted)]" />
        <input
          type="text"
          value={name}
          readOnly={!canWrite}
          onChange={(e) => setName(e.target.value)}
          onBlur={commitName}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape' && !isDraft) { setName(dataset.name || ''); e.currentTarget.blur(); } }}
          autoFocus={isDraft} // eslint-disable-line jsx-a11y/no-autofocus
          placeholder={isDraft ? 'Name this dataset' : undefined}
          aria-label="Dataset name"
          className="h-8 min-w-0 max-w-[280px] truncate rounded-md bg-transparent px-2 text-[13px] font-semibold text-[color:var(--text-primary)] outline-none placeholder:font-normal placeholder:text-[color:var(--text-disabled)] focus:bg-[color:var(--surface-base)] focus:ring-1 focus:ring-[color:var(--border-strong)]"
        />
        <div className="flex-1" />
        {gridDirty ? <span className="text-[11px] text-[color:var(--text-muted)]">Unsaved changes</span> : null}
        {showSave ? (
          <button type="button" onClick={saveGrid} disabled={savingGrid} className={primaryBtn}>
            {savingGrid ? 'Saving…' : 'Save'}
          </button>
        ) : null}
        {!isDraft && canWrite ? <LabelMenu datasetId={datasetId} datasetName={dataset.name} neutral={showSave} /> : null}
        {!isDraft ? (
          <div ref={more.box} className="relative">
            <button type="button" onClick={() => more.setOpen((v) => !v)} aria-label="More" aria-haspopup="menu" aria-expanded={more.open} className={`${activityToolbarButtonClass} !px-2`}>
              <MoreHorizontal size={14} />
            </button>
            {more.open ? (
              <div role="menu" aria-label="Dataset actions" className={menuClass}>
                {canWrite ? (
                  <MenuItem onClick={() => { more.setOpen(false); syncSources(); }} disabled={syncBusy || gridDirty || !syncableSources.length}
                    title={gridDirty ? 'Save your changes first' : (running ? 'A sync is already running' : (!syncableSources.length ? (agentSources.length ? 'Agent sources write when the agent runs. Sync cannot rerun them.' : 'No sources to sync') : 'Rerun every source. Rows already here are kept and not inserted again.'))}>
                    {syncBusy ? 'Syncing…' : 'Sync'}
                  </MenuItem>
                ) : null}
                <MenuItem onClick={() => { more.setOpen(false); setSourcesOpen(true); }}>Sources</MenuItem>
                {canWrite ? (
                  <MenuItem onClick={async () => {
                    more.setOpen(false);
                    try {
                      const pg = await createPlayground(`Playground · ${dataset.name || 'dataset'}`);
                      navigate(`/${encodeURIComponent(team)}/playground/${pg.id}?dataset=${encodeURIComponent(datasetId)}`);
                    } catch (e) { setError(e?.response?.data?.error || e.message); }
                  }}>Open in Playground</MenuItem>
                ) : null}
                <MenuItem onClick={() => { more.setOpen(false); setVersionsDialog(true); }}>Versions</MenuItem>
                <MenuItem onClick={() => { more.setOpen(false); setExportDialog({ itemIds: null }); }} disabled={rowCount === 0}>Export</MenuItem>
                <MenuItem onClick={() => { more.setOpen(false); gridRef.current?.download?.(); }} disabled={rowCount === 0}>Download</MenuItem>
                {dataset.can_delete ? <MenuItem danger onClick={() => { more.setOpen(false); handleDelete(); }}>Delete dataset</MenuItem> : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </header>

      {error ? (
        <div className="mx-4 mt-2 flex items-center gap-2 rounded-md border border-[color:var(--feedback-error)] bg-danger-soft px-3 py-2 text-[12px] text-danger">
          <AlertTriangle size={14} /> {error}
        </div>
      ) : null}
      {notice ? (
        <div role="status" className="mx-4 mt-2 rounded-md border border-[color:var(--border-default)] px-3 py-2 text-[12px] text-[color:var(--text-secondary)]">{notice}</div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <DatasetGrid
            key={gridKey}
            ref={gridRef}
            dataset={dataset}
            fillHeight
            readOnly={!canWrite}
            toolbarLeading={canWrite ? (
              <div ref={addMenu.box} className="relative">
                <button type="button" onClick={() => addMenu.setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={addMenu.open} className={addBtn}>
                  <Plus size={13} /> Add <ChevronDown className="h-3.5 w-3.5" />
                </button>
                {addMenu.open ? (
                  <div role="menu" aria-label="Add" className={addMenuClass}>
                    <MenuItem onClick={() => { addMenu.setOpen(false); gridRef.current?.openImport?.(); }}>Import</MenuItem>
                    <MenuItem onClick={() => { addMenu.setOpen(false); gridRef.current?.addRow?.(); }}>Add row</MenuItem>
                    <MenuItem onClick={() => { addMenu.setOpen(false); if (draftHasRows) return; setSourceDialog({}); }} disabled={draftHasRows} title={draftHasRows ? 'Save the dataset first' : undefined}>Add source</MenuItem>
                  </div>
                ) : null}
              </div>
            ) : null}
            onDirtyChange={setGridDirty}
            onError={failWith}
            onPageItems={onPageItems}
            selectedRowId={drawerRowId}
            onRowSelect={openRowDrawer}
            onSaved={isDraft ? undefined : onSaved}
            persist={isDraft ? persistDraft : undefined}
            onOpenOrigin={openOrigin}
            expiredItemIds={expiredItemIds}
            onExportSelection={isDraft ? undefined : (itemIds) => setExportDialog({ itemIds })}
          />
        </div>

        {drawerRowId && drawerContext ? (
          <DatasetRowDrawer
            embedded
            datasetId={datasetId}
            rowIndex={drawerContext.rowIndex}
            rowCount={drawerContext.rowCount}
            row={drawerContext.row}
            evaluations={drawerContext.row.evaluations}
            readOnly={!canWrite}
            initialTab={drawerTab}
            onClose={() => openRowDrawer(null)}
            onPrev={() => navigateDrawerRow(-1)}
            onNext={() => navigateDrawerRow(1)}
            onDelete={() => { gridRef.current?.deleteRow?.(drawerRowId); openRowDrawer(null); }}
            onRowChange={(patch) => { gridRef.current?.patchRow?.(drawerRowId, patch); refreshDrawerSnapshot(); }}
            onRefreshEvaluations={isDraft ? undefined : (itemId) => refreshEvals(itemId)}
            onEvaluationFeedback={onEvaluationFeedback}
            onOpenOrigin={openOrigin}
          />
        ) : null}
      </div>

      {saveDialog ? <SaveDatasetDialog initialName={name} onSave={saveDialogDone} onClose={() => setSaveDialog(false)} /> : null}
      {sourcesOpen ? (
        <SourcesDialog sources={sources} canWrite={canWrite} onClose={() => setSourcesOpen(false)}
          onEdit={(src) => setSourceDialog({ source: src })} onRun={sourceRun} onRemove={sourceRemove} />
      ) : null}
      {sourceDialog && isDraft ? (
        <SourceDialog newDataset initial={{ name: isPlaceholderName(name) ? '' : name.trim() }} onClose={() => setSourceDialog(null)} />
      ) : sourceDialog ? (
        <SourceDialog datasetId={datasetId} datasetName={dataset.name} source={sourceDialog.source || null}
          onClose={() => setSourceDialog(null)} onSaved={sourceSaved} />
      ) : null}
      {versionsDialog ? (
        <VersionsDialog datasetId={datasetId} versions={versions} canWrite={canWrite} onClose={() => setVersionsDialog(false)}
          onRestored={async () => { await load(); setGridKey((k) => k + 1); setNotice('Restored'); }} />
      ) : null}
      {exportDialog ? (
        <ExportDialog dataset={dataset} versions={versions} itemIds={exportDialog.itemIds} onClose={() => setExportDialog(null)} />
      ) : null}

      {originOverlay?.spanId ? (
        <TraceDetailPage spanId={originOverlay.spanId} teamId={team} onClose={() => setOriginOverlay(null)}
          onRunClick={(runId) => { setOriginOverlay(null); setRunOverlay({ runId, initialSpanId: originOverlay.spanId }); }} />
      ) : null}
      {conversationOverlay?.conversationId ? (
        <React.Suspense fallback={null}>
          <ConversationDetailDrawer conversationId={conversationOverlay.conversationId} teamId={team} onClose={() => setConversationOverlay(null)} />
        </React.Suspense>
      ) : null}
      {runOverlay?.runId ? (
        <AgentRunDetailPage runId={runOverlay.runId} teamId={team} initialSpanId={runOverlay.initialSpanId} onClose={() => setRunOverlay(null)} />
      ) : null}
    </div>
  );
}
